/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: GPT-5 Codex
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCdrFilterList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing CDR Filters list.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchCdrFilters } from '../api';
import { CdrFilter, ListCdrFiltersParams } from '../types';

interface UseCdrFilterListProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing CDR Filters list
 */
export function useCdrFilterList(props: UseCdrFilterListProps = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListCdrFiltersParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search
      };

      const result = await fetchCdrFilters(queryParams, effectiveDomainId);

      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cdrFilters.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, effectiveDomainId]);

  const availableColumns = useMemo(() => [
    { key: 'description', label: t('cdrFilters.columns.description'), visible: true, sortable: true },
    { key: 'enabled', label: t('cdrFilters.columns.enabled'), visible: true, sortable: true },
    { key: 'createdAt', label: t('cdrFilters.columns.createdAt'), visible: true, sortable: true },
    { key: 'actions', label: t('common.actions'), visible: true, sortable: false }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 25,
    moduleName: 'cdrFilters',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as CdrFilter[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

